/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect, useId, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/_new/Button';
import { moveToDown, moveToTop } from 'utils/array';
import { InputTextArea } from '../../_new/InputTextArea';
import { InputText } from '../../_new/InputText';

interface Section {
	title: string;
	options: string[];
}

type SectionedMultipleChoiceEditWrapperProps = {
	initialItems: Section[];
	initialLabel: string;
	initialText: string;
	required: boolean;
	hiddenFromUsers: boolean;
	minAnswers: number;
	maxAnswers: number;
	onSave: (
		items: Section[],
		label: string,
		text: string,
		required: boolean,
		hiddenFromUsers: boolean,
		min: number,
		max: number
	) => void;
	onCancel: () => void;
};

interface EditFormObject {
	option: string;
	sectionTitle: string;
	questionLabel: string;
	questionText: string;
	isRequired: boolean;
	hiddenFromUsers: boolean;
	minAnswers: number;
	maxAnswers: number;
}

export const SectionedMultipleChoiceEditWrapper: React.FC<SectionedMultipleChoiceEditWrapperProps> = ({
	initialItems,
	initialLabel,
	initialText,
	required,
	hiddenFromUsers,
	minAnswers,
	maxAnswers,
	onSave,
	onCancel,
}) => {
	const { t } = useTranslation();
	const [items, setItems] = useState<Section[]>(initialItems);
	const [editing, setEditing] = useState<{
		sectionIndex: number | null;
		optionIndex: number | null;
		editingSectionTitle: boolean;
	}>({ sectionIndex: null, optionIndex: null, editingSectionTitle: false });

	const requiredCheckboxId = useId();
	const hiddenFromUsersCheckboxId = useId();
	const inputRef = useRef<HTMLInputElement | null>(null);

	const { control, setValue, handleSubmit, getValues } = useForm<EditFormObject>({
		defaultValues: {
			questionText: initialText,
			questionLabel: initialLabel,
			isRequired: required,
			hiddenFromUsers,
			minAnswers,
			maxAnswers,
		},
	});

	useEffect(() => {
		setValue('questionLabel', initialLabel);
		setValue('questionText', initialText);
		setValue('isRequired', required);
		setValue('hiddenFromUsers', hiddenFromUsers);
		setValue('minAnswers', minAnswers);
		setValue('maxAnswers', maxAnswers);
	}, [initialLabel, initialText, required, minAnswers, maxAnswers, setValue]);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleSubmit(handleFormSubmit)();
		}
	};

	const handleAddOption = (sectionIndex: number) => {
		const currentOptionCount = items[sectionIndex].options.length;

		setItems((prevItems) => {
			const updatedSections = [...prevItems];
			updatedSections[sectionIndex].options.push('');
			return updatedSections;
		});

		setEditing({
			sectionIndex,
			optionIndex: currentOptionCount,
			editingSectionTitle: false,
		});

		setValue('option', '');
		setTimeout(() => inputRef.current?.focus(), 0);
	};

	const handleEditOption = (sectionIndex: number, optionIndex: number) => {
		const option = items[sectionIndex].options[optionIndex];
		setEditing({ sectionIndex, optionIndex, editingSectionTitle: false });
		setValue('option', option);
		setTimeout(() => inputRef.current?.focus(), 0);
	};

	const handleEditSectionTitle = (sectionIndex: number) => {
		const section = items[sectionIndex];
		setEditing({ sectionIndex, optionIndex: null, editingSectionTitle: true });
		setValue('sectionTitle', section.title);
		setTimeout(() => inputRef.current?.focus(), 0);
	};

	const handleDeleteOption = (sectionIndex: number, optionIndex: number) => {
		setItems((prevItems) => {
			const updatedSections = [...prevItems];
			updatedSections[sectionIndex].options.splice(optionIndex, 1);
			return updatedSections;
		});
		setEditing({ sectionIndex: null, optionIndex: null, editingSectionTitle: false });
		setValue('option', '');
	};

	const handleSectionMoveToTop = (sectionIndex: number) => {
		setItems((prev) => moveToTop(prev, sectionIndex));
	};

	const handleSectionMoveToDown = (sectionIndex: number) => {
		setItems((prev) => moveToDown(prev, sectionIndex));
	};

	const handleOptionMoveToTop = (sectionIndex: number, optionIndex: number) => {
		setItems((prev) => {
			const updatedSections = [...prev];
			updatedSections[sectionIndex] = {
				...updatedSections[sectionIndex],
				options: moveToTop(updatedSections[sectionIndex].options, optionIndex),
			};
			return updatedSections;
		});
	};

	const handleOptionMoveToDown = (sectionIndex: number, optionIndex: number) => {
		setItems((prev) => {
			const updatedSections = [...prev];
			updatedSections[sectionIndex] = {
				...updatedSections[sectionIndex],
				options: moveToDown(updatedSections[sectionIndex].options, optionIndex),
			};
			return updatedSections;
		});
	};

	const handleDeleteSection = (sectionIndex: number) => {
		setItems((prevItems) => prevItems.filter((_, index) => index !== sectionIndex));
	};

	const handleFormSubmit = (data: EditFormObject) => {
		setItems((prevItems) => {
			const updatedSections = [...prevItems];
			if (editing.editingSectionTitle && editing.sectionIndex !== null) {
				updatedSections[editing.sectionIndex].title = data.sectionTitle.trim();
			} else if (editing.sectionIndex !== null) {
				if (editing.optionIndex !== null) {
					updatedSections[editing.sectionIndex].options[editing.optionIndex] = data.option.trim();
				}
			}
			return updatedSections;
		});

		// Open add option automatically with focus
		if (editing.sectionIndex !== null) {
			handleAddOption(editing.sectionIndex);
		} else {
			setEditing({ sectionIndex: null, optionIndex: null, editingSectionTitle: false });
		}
		setValue('option', '');
		setValue('sectionTitle', '');
	};

	const handleAddSection = (e: React.MouseEvent) => {
		e.preventDefault();
		setItems((prevItems) => [...prevItems, { title: '', options: [] }]);
		setEditing({ sectionIndex: items.length, optionIndex: null, editingSectionTitle: true });
		setValue('sectionTitle', '');
		setTimeout(() => inputRef.current?.focus(), 0);
	};

	const handleSave = (data: EditFormObject) => {
		onSave(
			items,
			data.questionLabel,
			data.questionText,
			data.isRequired,
			data.hiddenFromUsers,
			data.minAnswers,
			data.maxAnswers
		);
	};

	return (
		<div className="flex flex-column align-items-start gap-2">
			<div className="field w-full mt-2">
				<Controller
					name="questionText"
					control={control}
					// rules={{ required: 'Question label is required' }}
					render={({ field, fieldState }) => (
						<>
							<label className="ml-2 field-label" htmlFor="questionText">
								{t('programPanel.editQuestion.questionContent')}
							</label>
							<InputText
								{...field}
								placeholder={t('programPanel.editQuestion.enterQuestionTextPlaceholder')}
							/>
							{fieldState.error && <small className="p-error">{fieldState.error.message}</small>}
						</>
					)}
				/>
			</div>
			<div className="field w-full mt-2">
				<Controller
					name="questionLabel"
					control={control}
					// rules={{ required: 'Question text is required' }}
					render={({ field, fieldState }) => (
						<>
							<label className="ml-2 field-label" htmlFor="questionLabel">
								{t('programPanel.editQuestion.additionalInformations')}
							</label>
							<InputTextArea
								{...field}
								placeholder={t('programPanel.editQuestion.enterQuestionLabelPlaceholder')}
							/>
							{fieldState.error && <small className="p-error">{fieldState.error.message}</small>}
						</>
					)}
				/>
			</div>
			<div>
				<div className="field-checkbox mb-3">
					<Controller
						name="isRequired"
						control={control}
						render={({ field }) => (
							<Checkbox
								id={requiredCheckboxId}
								{...field}
								inputId="required-checkbox"
								checked={field.value}
								onChange={(e) => setValue('isRequired', e.checked)}
							/>
						)}
					/>
					<label htmlFor={requiredCheckboxId} className="ml-2">
						{t('programPanel.editQuestion.isRequired')}
					</label>
				</div>
				<div className="mt-2 w-full mb-3">
					<Controller
						name="hiddenFromUsers"
						control={control}
						render={({ field }) => (
							<Checkbox
								id={hiddenFromUsersCheckboxId}
								{...field}
								inputId="hiddenFromUsers-checkbox"
								checked={field.value}
								onChange={(e) => setValue('hiddenFromUsers', e.checked)}
							/>
						)}
					/>
					<label className="ml-2 field-label" htmlFor={hiddenFromUsersCheckboxId}>
						{t('programPanel.editQuestion.hiddenFromUsers')}
					</label>
				</div>
			</div>
			<div className="flex gap-2">
				<div className="field w-full mb-3">
					<label htmlFor="min-answers" className="mr-2">
						{t('programPanel.editQuestion.minAnswers')}
					</label>
					<Controller
						name="minAnswers"
						control={control}
						render={({ field }) => (
							<InputNumber
								{...field}
								inputId="min-answers"
								onChange={({ value: newValue }) => {
									field?.onChange(newValue);
								}}
								mode="decimal"
								showButtons
								min={0}
								max={maxAnswers}
								step={1}
								inputClassName="w-4rem py-0"
							/>
						)}
					/>
				</div>
				<div className="field w-full mb-3">
					<label htmlFor="max-answers" className="mr-2">
						{t('programPanel.editQuestion.maxAnswers')}
					</label>
					<Controller
						name="maxAnswers"
						control={control}
						render={({ field }) => (
							<InputNumber
								{...field}
								inputId="max-answers"
								onChange={({ value: newValue }) => {
									field?.onChange(newValue);
								}}
								mode="decimal"
								showButtons
								min={minAnswers}
								max={100}
								step={1}
								inputClassName="w-4rem py-0"
							/>
						)}
					/>
				</div>
			</div>

			<div className="flex flex-column gap-4 align-items-start my-4">
				{items.map((section, sectionIndex) => {
					const isFirst = sectionIndex === 0;
					const isLast = sectionIndex === items.length - 1;
					return (
						<div
							key={`${sectionIndex}-${section.title}`}
							className="flex flex-column align-items-start gap-2"
						>
							{editing.sectionIndex === sectionIndex && editing.editingSectionTitle ? (
								<div className="flex fex-row justify-content-between gap-1 align-items-center mb-3">
									<Controller
										name="sectionTitle"
										control={control}
										rules={{ required: t('programPanel.editQuestion.sectionTitleRequired') }}
										render={({ field, fieldState }) => (
											<>
												<div className="flex flex-column gap-0">
													<InputText
														{...field}
														placeholder={t(
															'programPanel.editQuestion.sectionTitlePlaceholder'
														)}
														onKeyDown={handleKeyDown} // Add this to handle key down event
														ref={inputRef}
													/>
													{fieldState.error && (
														<small className="p-error">{fieldState.error.message}</small>
													)}
												</div>
												<Button
													label={t('actions.add')}
													icon="check"
													iconOnly
													variant="primary"
													variantSize="sm"
													onClick={handleSubmit(handleFormSubmit)}
													disabled={!field.value.trim()}
												/>
												<Button
													label={t('actions.cancel')}
													icon="times"
													iconOnly
													variant="danger-text"
													variantSize="sm"
													onClick={() =>
														setEditing({
															sectionIndex: null,
															optionIndex: null,
															editingSectionTitle: false,
														})
													}
												/>
											</>
										)}
									/>
								</div>
							) : (
								<div className="inline-flex gap-2 align-items-center">
									<h3 className="m-0">{section.title}</h3>
									<div className="flex flex-row gap-2">
										<Button
											label={t('actions.edit')}
											icon="pencil"
											iconOnly
											variant="primary-text"
											variantSize="sm"
											onClick={() => handleEditSectionTitle(sectionIndex)}
										/>
										<Button
											label={t('actions.remove')}
											icon="trash"
											iconOnly
											variant="danger-text"
											variantSize="sm"
											onClick={() => handleDeleteSection(sectionIndex)}
										/>
										<Button
											label={t('actions.moveToUp')}
											icon="arrow-up"
											iconOnly
											variant="grey-text"
											variantSize="sm"
											onClick={() => handleSectionMoveToTop(sectionIndex)}
											disabled={isFirst}
										/>
										<Button
											label={t('actions.moveToDown')}
											icon="arrow-down"
											iconOnly
											variant="grey-text"
											variantSize="sm"
											onClick={() => handleSectionMoveToDown(sectionIndex)}
											disabled={isLast}
										/>
									</div>
								</div>
							)}
							<ul className="list-none flex flex-column gap-1 ml-4">
								{section.options.map((option, optionIndex) => {
									const isFirst = optionIndex === 0;
									const isLast = optionIndex === section.options.length - 1;
									return (
										<li
											key={`${sectionIndex}-${section.title}-${optionIndex}-${option}`}
											className="flex justify-between items-center mb-2"
										>
											{editing.sectionIndex === sectionIndex &&
											editing.optionIndex === optionIndex ? (
												<Controller
													name="option"
													control={control}
													rules={{
														required: t('programPanel.editQuestion.optionIsRequired'),
													}}
													render={({ field, fieldState }) => (
														<div className="flex align-items-center gap-2">
															<InputText
																{...field}
																placeholder={t(
																	'programPanel.editQuestion.enterOptionPlaceholder'
																)}
																onKeyDown={handleKeyDown} // Add this to handle key down event
																ref={inputRef}
															/>
															{fieldState.error && (
																<small className="p-error">
																	{fieldState.error.message}
																</small>
															)}
															<Button
																label={t('actions.add')}
																icon="check"
																iconOnly
																variant="primary"
																variantSize="sm"
																onClick={handleSubmit(handleFormSubmit)}
																disabled={!field.value.trim()}
															/>
															<Button
																label={t('actions.cancel')}
																icon="times"
																iconOnly
																variant="danger-text"
																variantSize="sm"
																onClick={() =>
																	handleDeleteOption(sectionIndex, optionIndex)
																}
															/>
														</div>
													)}
												/>
											) : (
												<div className="inline-flex align-items-center gap-2">
													<span className="mr-2">{option}</span>
													<div className="inline-flex align-items-center">
														<Button
															label={t('actions.edit')}
															icon="pencil"
															iconOnly
															variant="primary-text"
															variantSize="sm"
															onClick={() => handleEditOption(sectionIndex, optionIndex)}
														/>
														<Button
															label={t('actions.remove')}
															icon="trash"
															iconOnly
															variant="danger-text"
															variantSize="sm"
															onClick={() =>
																handleDeleteOption(sectionIndex, optionIndex)
															}
														/>
														<Button
															label={t('actions.moveToUp')}
															icon="arrow-up"
															iconOnly
															variant="grey-text"
															variantSize="sm"
															onClick={() =>
																handleOptionMoveToTop(sectionIndex, optionIndex)
															}
															disabled={isFirst}
														/>
														<Button
															label={t('actions.moveToDown')}
															icon="arrow-down"
															iconOnly
															variant="grey-text"
															variantSize="sm"
															onClick={() =>
																handleOptionMoveToDown(sectionIndex, optionIndex)
															}
															disabled={isLast}
														/>
													</div>
												</div>
											)}
										</li>
									);
								})}
							</ul>
							<Button
								label={t('programPanel.editQuestion.addAnswer')}
								icon="plus"
								variant="primary-text"
								variantSize="sm"
								onClick={() => handleAddOption(sectionIndex)}
								disabled={editing.sectionIndex !== null || editing.optionIndex !== null}
								className="ml-4"
							/>
						</div>
					);
				})}

				<Button
					label={t('programPanel.editQuestion.addSection')}
					icon="plus"
					variant="primary-text"
					variantSize="sm"
					onClick={handleAddSection}
					disabled={editing.sectionIndex !== null || editing.optionIndex !== null}
				/>
			</div>

			<div className="flex flex-row gap-2">
				<Button
					label={t('actions.save')}
					variant="primary"
					variantSize="sm"
					onClick={handleSubmit(handleSave)}
				/>
				<Button label={t('actions.cancel')} variant="primary-text" variantSize="sm" onClick={onCancel} />
			</div>
		</div>
	);
};
